// slice-3454: the two PB app-chrome menu items that are ACTIONS, not screens.
//
// PB's Help menu carries two items whose whole job is to poke the running app rather than
// open a window (2026-08-25 nav-graybar review, rows N55 + N58):
//
//   Help > Help Contents (F1)  — m_index, m_phfc_main.srm:307044. Resolves the ACTIVE
//     sheet's window name and calls f_phfc_get_help(ls_window_name): context-sensitive help
//     for the screen you are standing on. PH10's equivalent is the living help panel
//     (shared/help/HelpPanel, ADR-0066), opened by ScreenHelpButton's listener — the `?` on
//     every screen's toolbar (dev-rules §4.15). It shows the screen's help, its PB window +
//     route, and the "Report an issue" form.
//   Help > Menu Navigator (F9) — m_menumapping. Opens PB's searchable tree of the whole
//     menu. PH10's equivalent is the sidebar NavSearch box (Ctrl+K).
//
// Neither has a route, so the nav leaf cannot be a link. Rather than teach the sidebar about
// each one, the leaf dispatches a window CustomEvent and the owning component listens — the
// same event a global accelerator fires, so F1/F9 keep working from anywhere in the shell
// exactly as they do in PB.
//
// Constants live in shared/ because both an app-shell file (Sidebar/NavSearch) and a
// shared/components file (ScreenHelpButton) need them, and shared must never import from
// features or app.

import { registerShortcut } from './shortcuts/shortcutRegistry';

/** Open the help panel for the current screen (PB Help > Help Contents, F1; ADR-0066). */
export const OPEN_SCREEN_HELP_EVENT = 'ph10:open-screen-help';

/** Focus the sidebar menu search box (PB Help > Menu Navigator, F9). */
export const FOCUS_NAV_SEARCH_EVENT = 'ph10:focus-nav-search';

export type AppShortcutEvent =
  | typeof OPEN_SCREEN_HELP_EVENT
  | typeof FOCUS_NAV_SEARCH_EVENT;

export function dispatchAppShortcut(event: AppShortcutEvent): void {
  window.dispatchEvent(new CustomEvent(event));
}

/**
 * Bind a PB function key to an app-shortcut event. Returns the unsubscribe function.
 *
 * <p>Deliberately ignores the keypress while focus is in a text input ONLY for keys a
 * browser/OS would otherwise own — F1 and F9 are safe everywhere, so the handler fires
 * regardless of focus, matching PB (where the accelerator belongs to the menu, not the
 * control). {@code preventDefault} stops the browser's own F1 help window.
 *
 * <p>slice-3562 (BigList #27): the two keys this function owned are now rows in
 * {@code shared/ui/shortcuts/pbShortcuts.ts} ({@code help.contents} = F1,
 * {@code m_phfc_main.srm:11797}; {@code help.navigator} = F9, {@code :11910}) and the
 * keystroke matching happens in the one shared dispatcher rather than in a listener per
 * caller. Behaviour is byte-for-byte what it was: unmodified key only, preventDefault, fires
 * regardless of focus (both rows are {@code allowInEditor} and neither is
 * {@code blockedByDialog}). The signature is kept so NavSearch and ScreenHelpButton did not
 * have to change — new callers should use {@code useShortcutAction} directly.
 */
export function bindFunctionKey(key: 'F1' | 'F9', run: () => void): () => void {
  return registerShortcut(key === 'F1' ? 'help.contents' : 'help.navigator', run);
}
